import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { To, useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { Buffer } from "buffer";

import { Button, Divider, FormHelperText, MenuItem, TextareaAutosize, TextField } from "@mui/material";

import { repoTypes } from "types";
import { groupsService, repositoriesService } from "services";
import { CodeRounded, Launch } from "@mui/icons-material";
import React from "react";
import CodeMirror from "@uiw/react-codemirror";

const NewVarForm = (props: {
  parent: "group" | "repo";
  parentId: string;
  redirectUrl: To | number;
}) => {
  const navigate = useNavigate();
  const { control, handleSubmit } = useForm<repoTypes.NewVarProps>();

  const {
    mutate: createRepoVar,
    isLoading: loadingRepo,
    isError: isRepoError,
    error: repoError,
  } = repositoriesService.useCreateVariable(props.parentId);
  const {
    mutate: createGroupVar,
    isLoading: loadingGroup,
    isError: isGroupError,
    error: groupError,
  } = groupsService.useCreateGroupVariable(props.parentId);

  const [fileType, setFileType] = React.useState("default");
  const handleFormSubmit: SubmitHandler<repoTypes.NewVarProps> = (data) => {
    data.value = Buffer.from(data.value).toString("base64");
    switch (props.parent) {
      case "group":
        createGroupVar(data, {
          onSuccess: () => {
            typeof props.redirectUrl === "number"
              ? navigate(props.redirectUrl)
              : navigate(props.redirectUrl);
          },
        });
        break;
      case "repo":
        createRepoVar(data, {
          onSuccess: () => {
            typeof props.redirectUrl === "number"
              ? navigate(props.redirectUrl)
              : navigate(props.redirectUrl);
          },
        });
        break;
    }
  };

  return (
    <form
      className="flex flex-col p-m gap-s min-w-[350px] max-w-min"
      onSubmit={handleSubmit(handleFormSubmit)}
    >
      <p className="text-l font-medium">New custom variable</p>
      <a href="https://docs.qaack.cloud/definitions/environment/#custom-defined-variables" target="_blank" rel="noreferrer" className="pb-none">
        <span className="text-s text-gray-500 pb-s">Variables documentation &nbsp; <Launch fontSize="inherit" /></span>
      </a>

      <span className="mr-m text-s text-justify text-gray-500 pl-s">
        There are two variable types, default and file. Default variables are inserted as is while file variables are inserted as a file and then the variable will hold the file path.
      </span>
      <Controller
        name="type"
        control={control}
        render={({ field }) => (
          <>
            <TextField defaultValue="default" size="small" select label="Type" {...field}>
              {["default", "file"].map((type) => (
                <MenuItem value={type} key={type} onClick={() => setFileType(type)}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
          </>
        )}
      />

      <span className="mr-m text-s text-justify text-gray-500 pl-s">
        A variable can be visible always or just on the build process.
      </span>
      <Controller
        name="visibility"
        control={control}
        render={({ field }) => (
          <>
            <TextField select defaultValue="always" size="small" label="Visibility" {...field}>
              {["always", "buildtime"].map((type) => (
                <MenuItem value={type} key={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
          </>
        )}
      />

      <Divider />

      <span className="mr-m text-s text-justify text-gray-500 pl-s">
        The name of the variable. This is the name that will be used in the environment. Spaces will be translated to "_".
      </span>
      <Controller
        name="name"
        control={control}
        render={({ field }) => (
          <TextField
            label="Name"
            size="small"
            {...field}
            onChange={(e) => field.onChange(e.target.value.replace(" ", "_").replace(/[a-z]/g, (match) => match.toUpperCase()).replace("-", "_"))}
          />
        )}
      />
      {fileType === "file" && (
        <Controller
          name="value"
          control={control}
          render={({ field }) => <CodeMirror maxWidth="350px" {...field} />}
        />)}
      {fileType === "default" && (
        <Controller
          name="value"
          control={control}
          render={({ field }) => <TextField size="small" label="Value" {...field} />}
        />
      )}

      <Button className="font-bold" variant="contained" type="submit">
        Create
      </Button>

      {(loadingRepo || loadingGroup) && <p>Loading...</p>}
      {(isRepoError || isGroupError) && (
        <FormHelperText error>
          {(repoError as AxiosError<any>)?.response?.data?.error}
          {(groupError as AxiosError<any>)?.response?.data?.error}
        </FormHelperText>
      )}
    </form>
  );
};

export default NewVarForm;
